import { useState, useEffect, useCallback } from 'react';
import {
  getWhatsAppStatus,
  startWhatsAppSession,
  getWhatsAppQRCode,
  logoutWhatsApp,
  WhatsAppStatus
} from '../utils/whatsappApi';

// Função utilitária para buscar telefones conectados de múltiplos agentes
export const getConnectedPhonesForAgents = async (agentIds: string[]): Promise<Record<string, string>> => {
  const connectedPhones: Record<string, string> = {};
  
  try {
    await Promise.all(agentIds.map(async (agentId) => {
      try {
        const status = await getWhatsAppStatus(agentId);
        
        // Para WuzAPI: verificar isConnected && loggedIn && phoneNumber
        if (status.isConnected && status.loggedIn && status.phoneNumber) {
          connectedPhones[agentId] = status.phoneNumber;
        }
        // Para Z-API: o getWhatsAppStatus já detecta Z-API e retorna phoneNumber no jid
        // mesmo quando isConnected = false, então verificamos apenas se tem phoneNumber
        else if (status.phoneNumber) {
          connectedPhones[agentId] = status.phoneNumber;
        }
      } catch {}
    }));
  } catch {}
  
  return connectedPhones;
};

interface UseWhatsAppStatusProps {
  agentId: string;
  checkInterval?: number;
}

interface UseWhatsAppStatusReturn {
  status: WhatsAppStatus;
  isLoading: boolean;
  isCheckingStatus: boolean;
  qrCodeImage: string | null;
  qrCodeError: string | null;
  isDialogOpen: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  openQRDialog: () => Promise<void>;
  closeQRDialog: () => void;
  generateNewQRCode: () => Promise<void>;
}

export function useWhatsAppStatus({ agentId, checkInterval = 5 * 60 * 1000 }: UseWhatsAppStatusProps): UseWhatsAppStatusReturn {
  const [status, setStatus] = useState<WhatsAppStatus>({ isConnected: false, loggedIn: false, phoneNumber: null });
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);
  const [qrCodeError, setQrCodeError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const s = await getWhatsAppStatus(agentId);
      setStatus(s);
    } catch (e) {
      // ignore
    }
  }, [agentId]);

  const checkStatusPeriodically = useCallback(() => {
    if (checkInterval <= 0) {
      return null;
    }
    const interval = setInterval(async () => {
      setIsCheckingStatus(true);
      await fetchStatus();
      setIsCheckingStatus(false);
    }, checkInterval);
    return interval as unknown as NodeJS.Timeout;
  }, [fetchStatus, checkInterval]);

  const closeQRDialog = useCallback(() => {
    setIsDialogOpen(false);
    setQrCodeImage(null);
    setQrCodeError(null);
  }, []);

  const generateNewQRCode = useCallback(async () => {
    try {
      const qr = await getWhatsAppQRCode(agentId);
      if (qr) {
        setQrCodeImage(qr);
        setQrCodeError(null);
      } else {
        throw new Error('QR Code não encontrado');
      }
    } catch (e) {
      setQrCodeError('Erro ao gerar QR Code. Tente novamente.');
    }
  }, [agentId]);

  const connect = useCallback(async () => {
    if (!agentId) return;
    setIsLoading(true);
    try {
      const qrCode = await startWhatsAppSession(agentId);
      if (qrCode) {
        setQrCodeImage(qrCode);
        setQrCodeError(null);
        setIsDialogOpen(true);
      } else {
        await generateNewQRCode();
        setIsDialogOpen(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [agentId, generateNewQRCode]);

  const disconnect = useCallback(async () => {
    if (!agentId) return;
    setIsLoading(true);
    try {
      await logoutWhatsApp(agentId);
      await fetchStatus();
    } finally {
      setIsLoading(false);
    }
  }, [agentId, fetchStatus]);

  const openQRDialog = useCallback(async () => {
    setIsDialogOpen(true);
    setIsLoading(true);
    try {
      const qrCode = await startWhatsAppSession(agentId);
      if (qrCode) {
        setQrCodeImage(qrCode);
        setQrCodeError(null);
      } else {
        await generateNewQRCode();
      }
    } finally {
      setIsLoading(false);
    }
  }, [agentId, generateNewQRCode]);

  useEffect(() => {
    if (!agentId) return;
    let intervalId: NodeJS.Timeout | null = null;
    (async () => {
      setIsLoading(true);
      await fetchStatus();
      setIsLoading(false);
      intervalId = checkStatusPeriodically();
    })();
    return () => {
      if (intervalId) clearInterval(intervalId as unknown as number);
    };
  }, [agentId, fetchStatus, checkStatusPeriodically]);

  useEffect(() => {
    if (isDialogOpen && agentId) {
      const t = setInterval(async () => {
        const current = await getWhatsAppStatus(agentId);
        setStatus(current);
        if (current.isConnected && current.loggedIn) closeQRDialog();
      }, 3000);
      return () => clearInterval(t);
    }
  }, [isDialogOpen, agentId, closeQRDialog]);

  return {
    status,
    isLoading,
    isCheckingStatus,
    qrCodeImage,
    qrCodeError,
    isDialogOpen,
    connect,
    disconnect,
    openQRDialog,
    closeQRDialog,
    generateNewQRCode,
  };
}


